import React from 'react';
import { createPortal } from 'react-dom';

const formatINR = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatUSD = (n) => `$${(Number(n) || 0).toFixed(2)}`;

/**
 * Rate pill for the Cost Panel: always shows today's live rate. If the rate
 * actually used for this quote (usedGoldRate/usedDollarRate) was loaded from
 * a backdated Estimate/Bill Date and differs from today's, shows an alert
 * with the rate that's actually in effect.
 */
export const RatePill = ({
  todayGoldRate,
  usedGoldRate,
  todayDollarRate = null,
  usedDollarRate = null,
  usedDate = '',
}) => {
  const showDollar = todayDollarRate !== null && usedDollarRate !== null;
  const goldDiffers = todayGoldRate != null && Math.abs((todayGoldRate || 0) - (usedGoldRate || 0)) > 0.005;
  const dollarDiffers = showDollar && Math.abs((todayDollarRate || 0) - (usedDollarRate || 0)) > 0.005;
  const differs = goldDiffers || dollarDiffers;
  const displayGold = todayGoldRate != null ? todayGoldRate : usedGoldRate;
  const displayDollar = todayDollarRate != null ? todayDollarRate : usedDollarRate;

  return (
    <div className="mb-2 space-y-1">
      <div className="px-2 py-1 rounded bg-blue-50 border border-blue-200 text-xs text-blue-700 flex items-center justify-between">
        <span>📌 Today's Rate</span>
        <span className="font-semibold">
          ₹{(displayGold || 0).toLocaleString('en-IN')}/g
          {showDollar && ` · ₹${(displayDollar || 0).toLocaleString('en-IN')}/$`}
        </span>
      </div>
      {differs && (
        <div className="px-2 py-1 rounded bg-red-50 border border-red-200 text-xs text-red-700 flex items-center justify-between">
          <span>⚠️ Using {usedDate || 'loaded'} rate</span>
          <span className="font-semibold">
            ₹{(usedGoldRate || 0).toLocaleString('en-IN')}/g
            {showDollar && ` · ₹${(usedDollarRate || 0).toLocaleString('en-IN')}/$`}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Shared "hold-to-peek" Cost Panel used across Estimate and Billing screens.
 * Callers own the cost calculation (which differs by screen); this component
 * only renders the breakdown/summary/profit UI from the computed values.
 */
const CostPanel = ({
  title = null,
  rows = [],
  ratePill = null,
  quotedCost = 0,
  actualCost = 0,
  quotedCostUSD = null,
  actualCostUSD = null,
  profit = 0,
  profitPct = 0,
  profitUSD = null,
  usePortal = false,
  positionClassName = 'top-[60px] right-4',
  widthClassName = 'w-80',
  textSizeClassName = 'text-sm',
}) => {
  const showUSD = quotedCostUSD !== null && actualCostUSD !== null;

  const panel = (
    <div
      className={`print:hidden fixed ${positionClassName} z-50 ${widthClassName} p-4 rounded-xl border-2 border-amber-400 bg-amber-50/95 backdrop-blur-sm shadow-2xl select-none`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-bold text-amber-700 uppercase tracking-widest">🔒 Cost Panel</span>
        <span className="text-xs text-amber-500">Hold ` to view</span>
      </div>
      {title && (
        <div className="mb-3 text-sm font-bold text-amber-900 truncate" title={title}>{title}</div>
      )}

      {ratePill}

      <div className={`grid grid-cols-2 gap-x-4 gap-y-1 ${textSizeClassName}`}>
        {rows.map((row, i) => (
          <React.Fragment key={i}>
            <span className={row.muted ? 'text-gray-400 text-xs' : 'text-gray-600'}>{row.label}</span>
            <span className={row.muted ? 'text-gray-400 text-xs text-right' : 'text-right font-medium'}>
              {row.raw !== undefined ? row.raw : formatINR(row.value)}
            </span>
          </React.Fragment>
        ))}
      </div>

      <div className={`border-t border-amber-300 mt-3 pt-3 space-y-2 ${textSizeClassName}`}>
        <div className="flex items-center justify-between">
          <span className="font-bold text-blue-700">Quoted Cost</span>
          <span className="font-bold text-blue-700">
            {formatINR(quotedCost)}
            {showUSD && <span className="text-blue-400 font-normal"> / {formatUSD(quotedCostUSD)}</span>}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-bold text-gray-800">Actual Cost</span>
          <span className="font-bold text-gray-800">
            {formatINR(actualCost)}
            {showUSD && <span className="text-gray-400 font-normal"> / {formatUSD(actualCostUSD)}</span>}
          </span>
        </div>
      </div>

      <div
        className={`mt-3 flex items-center justify-between rounded-lg px-3 py-2 ${
          profit >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}
      >
        <span className="font-bold text-sm">{profit >= 0 ? 'Profit' : 'Loss'}</span>
        <span className="font-bold text-sm">
          {formatINR(Math.abs(Math.ceil(profit)))}
          {profitUSD !== null && <span className="opacity-60 font-normal"> / {formatUSD(Math.abs(profitUSD))}</span>}
          {' '}({profitPct}%)
        </span>
      </div>
    </div>
  );

  return usePortal ? createPortal(panel, document.body) : panel;
};

export default CostPanel;
